import { Calendar, ChevronDown, X } from 'lucide-react'
import { useCallback, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { useInfiniteEconomicCyclesQuery } from '@/hooks/business/useEconomicCyclesQuery'
import { cn } from '@/lib/utils'
import { useAreasQuery } from '@/hooks/business/useAreasQuery'

interface AreaSalesFilterProps {
  title: string
  icon?: React.ReactNode
  selectedValue: number | null
  onSelectionChange: (_value: number | null) => void
  placeholder?: string
  className?: string
}

export const AreaSalesFilter = ({
  title,
  icon,
  selectedValue,
  onSelectionChange,
  placeholder = 'Seleccionar punto de venta...',
  className,
}: AreaSalesFilterProps) => {
  const [open, setOpen] = useState(false)

  const { businessData, isLoadingBusinessData } = useBusinessDataQuery({})

  const { areas: areaSales = [], isLoadingAreas } = useAreasQuery({
    params: {
      businessId: businessData?.id ?? 0,
    },
    options: {
      enabled: !!businessData?.id && !isLoadingBusinessData,
    },
  })

  const handleSelect = useCallback(
    (areaId: number) => {
      onSelectionChange(areaId === selectedValue ? null : areaId)
      setOpen(false)
    },
    [selectedValue, onSelectionChange]
  )

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          aria-label={title}
          className={cn('gap-1.5', className)}
          variant="outline"
        >
          {icon ? icon : <Calendar className="h-4 w-4" />}
          <span>{title}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
          {selectedValue && (
            <Badge className="ml-1 rounded-full px-1.5" variant="secondary">
              1
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[300px] p-0">
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>
              {isLoadingAreas
                ? 'Cargando puntos de venta...'
                : 'No se encontraron puntos de venta.'}
            </CommandEmpty>
            <CommandGroup>
              {isLoadingAreas
                ? // Loading skeletons
                  Array.from({ length: 3 }).map((_, i) => (
                    <div className="flex items-center space-x-2 p-2" key={i}>
                      <Skeleton className="h-4 w-4" />
                      <div className="flex-1 space-y-1">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-3 w-2/3" />
                      </div>
                    </div>
                  ))
                : areaSales.map((area) => (
                    <CommandItem
                      className="flex cursor-pointer items-center justify-between p-2"
                      key={area.id}
                      onSelect={() => handleSelect(area.id)}
                    >
                      <div className="flex-1">
                        <div className="font-medium">{area.name} </div>
                      </div>
                      {selectedValue === area.id && (
                        <div className="ml-2 h-2 w-2 rounded-full bg-blue-600" />
                      )}
                    </CommandItem>
                  ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// Selection Summary Component for displaying active filter
export const AreaSalesSelection = ({
  selectedValue,
  onClear,
}: {
  selectedValue: number | null
  onClear: () => void
}) => {
  const { businessData, isLoadingBusinessData } = useBusinessDataQuery({})

  const { economicCycles = [] } = useInfiniteEconomicCyclesQuery({
    businessId: businessData?.id ?? 0,
    params: {
      dateFrom: new Date(Date.now() - 6 * 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
      dateTo: new Date().toISOString().split('T')[0],
    },
    options: {
      enabled: Boolean(businessData?.id && !isLoadingBusinessData),
    },
  })

  if (!selectedValue) return null

  const selectedCycle = economicCycles.find(
    (cycle) => cycle.id === selectedValue
  )

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium">Ciclo Económico:</span>
      <Badge
        className="flex items-center gap-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
        variant="default"
      >
        <span>
          {selectedCycle
            ? `${selectedCycle.name || `Ciclo ${selectedCycle.id}`}`
            : `Ciclo ${selectedValue}`}
        </span>
        <X className="h-3 w-3 cursor-pointer" onClick={onClear} />
      </Badge>
    </div>
  )
}
